import React, { useState, useEffect, useMemo } from 'react';
import { MOCK_AGREEMENTS } from '../../data/mockAgreements';

const STATUS_DISPLAY = {
  DRAFT: { label: 'Draft', className: 'pending' },
  PENDING_REVIEW: { label: 'Pending Review', className: 'review' },
  NEEDS_CONTRACTS_REVIEW: { label: 'Contracts Review', className: 'review' },
  FULLY_EXECUTED: { label: 'Executed', className: 'active' },
  EXPIRED: { label: 'Expired', className: 'critical' },
};

const TYPE_DISPLAY = {
  NDA: { label: 'NDA', className: 'accepted' },
  TA: { label: 'TA', className: 'completed' },
};

function formatDate(iso) {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/** Build mock timeline entries from the agreement record. */
function buildTimeline(item) {
  if (!item) return [];
  const entries = [];

  entries.push({
    color: 'blue',
    text: `Created by ${item.createdBy}`,
    time: formatDate(item.createdAt),
  });

  if (item.status === 'PENDING_REVIEW' || item.status === 'NEEDS_CONTRACTS_REVIEW' ||
      item.status === 'FULLY_EXECUTED' || item.status === 'EXPIRED') {
    entries.push({
      color: 'yellow',
      text: 'Submitted for review',
      time: formatDate(item.updatedAt !== item.createdAt ? item.updatedAt : item.createdAt),
    });
  }

  if (item.status === 'NEEDS_CONTRACTS_REVIEW') {
    entries.push({
      color: 'yellow',
      text: 'Escalated to contracts team',
      time: formatDate(item.updatedAt),
    });
  }

  if (item.status === 'FULLY_EXECUTED') {
    entries.push({
      color: 'green',
      text: 'Agreement fully executed',
      time: formatDate(item.signedDate),
    });
  }

  if (item.status === 'EXPIRED') {
    entries.push({
      color: 'red',
      text: 'Agreement expired',
      time: formatDate(item.expirationDate),
    });
  }

  return entries;
}

function AgreementDetail({ agreementId, onNavigate, user }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionMessage, setActionMessage] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      const found = MOCK_AGREEMENTS.find((a) => a.id === agreementId);
      setItem(found ? { ...found } : null);
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [agreementId]);

  const timeline = useMemo(() => buildTimeline(item), [item]);

  const handleAction = (newStatus, message) => {
    if (!item) return;
    setItem((prev) => ({ ...prev, status: newStatus, updatedAt: new Date().toISOString() }));
    setActionMessage(message);
    setTimeout(() => setActionMessage(''), 4000);
  };

  // Loading
  if (loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        color: 'var(--bcore-text-dim)',
        fontSize: '13px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      }}>
        Loading agreement...
      </div>
    );
  }

  // Not found
  if (!item) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--bcore-text-dim)', fontSize: '13px' }}>
        <div style={{ textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
          Agreement not found
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => onNavigate('agreement-review')}>
          Back to Agreement Review
        </button>
      </div>
    );
  }

  const statusInfo = STATUS_DISPLAY[item.status] || { label: item.status, className: '' };
  const typeInfo = TYPE_DISPLAY[item.type] || { label: item.type, className: '' };

  // Role-based access for approval routing
  const isAdmin = user?.groups?.includes('admin') ?? false;
  const isGrowthUser = user?.groups?.includes('growth') ?? false;
  const isContractsUser = user?.groups?.includes('security') ?? false; // contracts maps to security group for now
  const canApproveStandard = isAdmin || isGrowthUser; // growth leadership can approve standard-terms NDAs
  const canApproveAll = isAdmin || isContractsUser; // contracts can approve anything
  const isNonStandard = item.termsType === 'non-standard';

  return (
    <div>
      {/* Back Link */}
      <button className="form-back-link" onClick={() => onNavigate('agreement-review')}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to Agreement Review
      </button>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <span className={`badge-status ${typeInfo.className}`}>{typeInfo.label}</span>
          <h2 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 600,
            color: 'var(--bcore-text-primary)',
          }}>
            {item.counterpartyName}
          </h2>
          <span style={{ fontSize: '13px', color: 'var(--bcore-text-dim)' }}>
            ({item.counterpartyShortName})
          </span>
        </div>
        <div className="ws-detail-meta">
          <span className={`badge-status ${statusInfo.className}`}>
            <span className="badge-dot"></span>
            {statusInfo.label}
          </span>
          {item.opportunityName && (
            <span className="ws-detail-meta-item">{item.opportunityName}</span>
          )}
          <span className="ws-detail-meta-item">Owner: {item.opportunityOwner}</span>
          <span className={`tag${item.termsType === 'non-standard' ? ' accent' : ''}`}>
            {item.termsType} terms
          </span>
          <span style={{
            fontSize: '10px',
            color: 'var(--bcore-text-dim)',
            background: 'var(--bcore-bg-tertiary, rgba(255,255,255,0.05))',
            padding: '2px 8px',
            borderRadius: '4px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '4px', verticalAlign: '-1px' }}>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            Contracts visibility
          </span>
        </div>
      </div>

      {/* Action Message */}
      {actionMessage && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(52,211,153,0.1)',
          border: '1px solid rgba(52,211,153,0.25)',
          borderRadius: '8px',
          fontSize: '12px',
          color: 'var(--status-active)',
          marginBottom: '20px',
        }}>
          {actionMessage}
        </div>
      )}

      {/* Split View */}
      <div className="ws-detail-body">
        {/* Left Panel — Metadata */}
        <div className="ws-detail-form">
          <div className="form-card">
            <div className="form-section">
              <div className="form-section-title">Agreement Details</div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}>
                <MetaField label="Counterparty" value={item.counterpartyName} />
                <MetaField label="Short Name" value={item.counterpartyShortName} />
                <MetaField label="Opportunity" value={item.opportunityName} />
                <MetaField label="Opportunity Owner" value={item.opportunityOwner} />
                <MetaField label="Type" value={item.type} />
                <MetaField label="Terms" value={item.termsType} />
                <MetaField label="Term Duration" value={item.termDuration} />
                <MetaField label="Created By" value={item.createdBy} />
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">Dates</div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
              }}>
                <MetaField label="Created" value={formatDateTime(item.createdAt)} />
                <MetaField label="Last Updated" value={formatDateTime(item.updatedAt)} />
                {(item.status === 'FULLY_EXECUTED' || item.status === 'EXPIRED') && (
                  <>
                    <MetaField label="Signed Date" value={formatDate(item.signedDate)} />
                    <MetaField label="Expiration Date" value={formatDate(item.expirationDate)} />
                  </>
                )}
              </div>
            </div>

            {/* Status-Dependent Actions (role-gated) */}
            <div className="form-actions">
              {item.status === 'DRAFT' && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleAction('PENDING_REVIEW', 'Agreement submitted for review.')}
                >
                  Submit for Review
                </button>
              )}

              {item.status === 'PENDING_REVIEW' && (canApproveStandard || canApproveAll) && (
                <>
                  {/* Growth users can escalate to contracts */}
                  {(isGrowthUser || isAdmin) && (
                    <button
                      className="btn btn-outline"
                      onClick={() => handleAction('NEEDS_CONTRACTS_REVIEW', 'Escalated to contracts team.')}
                    >
                      Escalate to Contracts
                    </button>
                  )}
                  {/* Approve: only for standard terms (growth), or contracts/admin for any */}
                  {(canApproveAll || (canApproveStandard && !isNonStandard)) && (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleAction('FULLY_EXECUTED', 'Agreement approved and marked as executed.')}
                    >
                      Approve
                    </button>
                  )}
                </>
              )}

              {/* Non-standard pending review: growth-only users must escalate */}
              {item.status === 'PENDING_REVIEW' && isNonStandard && isGrowthUser && !isAdmin && !isContractsUser && (
                <div style={{
                  fontSize: '11px',
                  color: 'var(--status-pending)',
                  fontStyle: 'italic',
                  marginTop: '4px',
                }}>
                  Non-standard terms require contracts approval
                </div>
              )}

              {item.status === 'PENDING_REVIEW' && !canApproveStandard && !canApproveAll && (
                <div style={{
                  padding: '10px 14px',
                  background: 'rgba(251,191,36,0.08)',
                  border: '1px solid rgba(251,191,36,0.2)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--status-pending)',
                }}>
                  Awaiting review by growth leadership
                </div>
              )}

              {item.status === 'NEEDS_CONTRACTS_REVIEW' && canApproveAll && (
                <>
                  <button
                    className="btn btn-outline"
                    onClick={() => handleAction('PENDING_REVIEW', 'Sent back for revision.')}
                  >
                    Send Back
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleAction('FULLY_EXECUTED', 'Agreement approved by contracts and marked as executed.')}
                  >
                    Approve
                  </button>
                </>
              )}

              {item.status === 'NEEDS_CONTRACTS_REVIEW' && !canApproveAll && (
                <div style={{
                  padding: '10px 14px',
                  background: 'rgba(251,191,36,0.08)',
                  border: '1px solid rgba(251,191,36,0.2)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'var(--status-pending)',
                }}>
                  Awaiting review by contracts team
                </div>
              )}

              {item.status === 'FULLY_EXECUTED' && (
                <button className="btn btn-outline" onClick={() => {}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel — Timeline & Notes */}
        <div className="ws-detail-document">
          <div className="form-card">
            <div className="form-section">
              <div className="form-section-title">Activity</div>
              {timeline.length > 0 ? (
                <div className="activity-feed">
                  {timeline.map((entry, i) => (
                    <div className="activity-item" key={i}>
                      <span className={`activity-dot ${entry.color}`}></span>
                      <span className="activity-text">{entry.text}</span>
                      <span className="activity-time">{entry.time}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--bcore-text-dim)' }}>
                  No activity recorded.
                </div>
              )}
            </div>

            <div className="form-section">
              <div className="form-section-title">Notes</div>
              {item.notes ? (
                <div style={{
                  fontSize: '13px',
                  color: 'var(--bcore-text-muted)',
                  lineHeight: '1.5',
                }}>
                  {item.notes}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--bcore-text-dim)' }}>
                  No notes.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Reusable metadata field display (label above value). */
function MetaField({ label, value }) {
  return (
    <div>
      <div style={{
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: 'var(--bcore-text-dim)',
        marginBottom: '4px',
        fontWeight: 600,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '13px',
        color: 'var(--bcore-text-primary)',
      }}>
        {value || '\u2014'}
      </div>
    </div>
  );
}

export default AgreementDetail;
